// src/slices/moduleManagement/thunk.ts
// Async thunks cho Quản lý Module

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getModules,
  getModuleById,
  getModulePermissions,
  createModule as createModuleAPI,
  updateModule as updateModuleAPI,
  deleteModule as deleteModuleAPI,
  createPermission as createPermissionAPI,
  deletePermission as deletePermissionAPI,
} from 'helpers/fakebackend_helper';
import { CreateModuleDto, CreatePermissionDto, UpdateModuleDto } from 'pages/ModuleManagement/types';

// Lấy danh sách modules — includeInactive=true để màn quản trị thấy cả module đã tắt
export const fetchModules = createAsyncThunk(
  'ModuleManagement/fetchModules',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await getModules(true as any);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi tải danh sách module');
    }
  }
);

export const fetchModuleDetail = createAsyncThunk(
  'ModuleManagement/fetchModuleDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getModuleById(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi tải chi tiết module');
    }
  }
);

interface CreateModulePayload {
  module: CreateModuleDto;
  // Nếu có, tạo luôn permission đầu tiên cho module vừa tạo (ModuleId gán sau khi biết module.id)
  permission?: Omit<CreatePermissionDto, 'moduleId'>;
}

export const createNewModule = createAsyncThunk(
  'ModuleManagement/createModule',
  async ({ module, permission }: CreateModulePayload, { rejectWithValue }) => {
    try {
      const moduleResponse = await createModuleAPI(module);
      const moduleId = (moduleResponse.data ?? moduleResponse) as number;

      if (permission) {
        await createPermissionAPI({ ...permission, moduleId });
      }

      return moduleId;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi tạo module');
    }
  }
);

export const updateExistingModule = createAsyncThunk(
  'ModuleManagement/updateModule',
  async (data: UpdateModuleDto, { rejectWithValue }) => {
    try {
      const response = await updateModuleAPI(data.id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi cập nhật module');
    }
  }
);

export const deleteModuleById = createAsyncThunk(
  'ModuleManagement/deleteModule',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteModuleAPI(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi xóa module');
    }
  }
);

// Lấy danh sách permission của module — dùng cho tab "Tạo Permission" ở form sửa
export const fetchModulePermissions = createAsyncThunk(
  'ModuleManagement/fetchModulePermissions',
  async (moduleId: number, { rejectWithValue }) => {
    try {
      const response = await getModulePermissions(moduleId);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi tải danh sách permission');
    }
  }
);

// Thêm permission mới cho module đã tồn tại (dùng ở tab "Tạo Permission" của form sửa)
// Backend CreatePermissionCommand chỉ trả về id (long), nên tự ghép lại thành PermissionItem
// đầy đủ từ data đã gửi để hiển thị ngay trong danh sách mà không cần gọi lại API.
export const addPermissionToModule = createAsyncThunk(
  'ModuleManagement/addPermissionToModule',
  async (data: CreatePermissionDto, { rejectWithValue }) => {
    try {
      const response = await createPermissionAPI(data);
      const newId = (response.data ?? response) as number;
      return { id: newId, ...data };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi tạo permission');
    }
  }
);

// Xóa permission (soft delete) — dùng ở tab "Tạo Permission" của form sửa
export const removePermission = createAsyncThunk(
  'ModuleManagement/removePermission',
  async (id: number, { rejectWithValue }) => {
    try {
      await deletePermissionAPI(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Có lỗi xảy ra khi xóa permission');
    }
  }
);
